/**
 * Folha de conflito.
 *
 * Aparência deliberadamente NEUTRA, não destrutiva: nenhuma das duas escolhas
 * é um erro, e pintar a folha de vermelho sugeriria que o usuário fez algo
 * errado ao editar offline — que é o uso normal do produto.
 *
 * As duas versões aparecem lado a lado, e a decisão é sempre do usuário:
 * nada é resolvido automaticamente (PRD §17.4).
 */
import type { Mutation } from '@nv/offline';

import { localDb } from '../features/roteiro/persistence';
import { useSyncStore } from '../sync/store';

/**
 * Campos que a pessoa reconhece. Mostrar `itineraryDayId` ou `updatedBy` ao
 * lado da própria edição não ajuda ninguém a decidir.
 */
const RÓTULOS: Record<string, string> = {
  title: 'Título',
  startTime: 'Horário',
  status: 'Status',
  locationName: 'Local',
  notes: 'Notas',
};

function descreve(valor: unknown): string {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor !== 'object') return String(valor);

  const linhas = Object.entries(valor as Record<string, unknown>)
    .filter(([chave]) => chave in RÓTULOS)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([chave, v]) => `${RÓTULOS[chave]}: ${String(v)}`);

  return linhas.length > 0 ? linhas.join('\n') : '—';
}

function resumoDaMinha(mutation: Mutation): string {
  const payload = mutation.payload as { patch?: Record<string, unknown> };
  return descreve(payload.patch ?? mutation.payload);
}

export function ConflictSheet() {
  const conflicted = useSyncStore((s) => s.conflicted);
  const resolve = useSyncStore((s) => s.resolve);

  const primeiro = conflicted[0];
  if (!primeiro) return null;

  const decidir = async (resolution: 'keep_server' | 'apply_mine') => {
    const db = await localDb();
    await resolve(db.outbox, primeiro.mutationId, resolution);
    window.dispatchEvent(new CustomEvent('nv:outbox-changed'));
  };

  return (
    <div className="sheet conflict" role="dialog" aria-modal="true" aria-labelledby="conflito-titulo">
      <div className="sheet__panel">
        <h2 className="sheet__title" id="conflito-titulo">
          Esta atividade mudou
        </h2>
        <p className="sheet__text">
          {primeiro.conflict?.message ??
            'Alguém alterou esta atividade enquanto você estava sem conexão.'}
        </p>

        <div className="conflict__pair">
          <div className="conflict__side">
            <span className="nv-section-label">Versão atual</span>
            <p className="conflict__value">{descreve(primeiro.conflict?.serverState)}</p>
          </div>
          <div className="conflict__side">
            <span className="nv-section-label">Minhas alterações</span>
            <p className="conflict__value">{resumoDaMinha(primeiro)}</p>
          </div>
        </div>

        <button
          type="button"
          className="nv-btn nv-btn--secondary nv-btn--block"
          onClick={() => void decidir('keep_server')}
        >
          Manter versão atual
        </button>
        <button
          type="button"
          className="nv-btn nv-btn--secondary nv-btn--block"
          onClick={() => void decidir('apply_mine')}
        >
          Aplicar minhas alterações
        </button>

        {conflicted.length > 1 && (
          <p className="conflict__rest">
            Mais {conflicted.length - 1} para revisar depois desta.
          </p>
        )}
      </div>
    </div>
  );
}
